import { computed } from 'vue';
import useAcvStore from '@/composables/useAcvStore';

/**
 * Composable interface to the Vuex `authorization` module.
 *
 * Exposes all authorization state and getters as reactive `computed` refs,
 * and all actions as plain async wrappers so callers never import the store directly.
 *
 * @returns {object} Reactive refs for state/getters and action dispatchers.
 *
 * @example
 * const { isAuthorized, userRole, loginUser } = useAcvAuth();
 */
const useAcvAuth = () => {
  const store = useAcvStore();

  // State
  const errors = computed(() => store.state.authorization.errors);
  const user = computed(() => store.state.authorization.user);

  /** `true` once the auth module has finished initializing (token restore + user hydration). */
  const ready = computed(() => store.state.authorization.ready);

  /** User preference settings grouped by category. Use `userSettings(category)` to read a slice. */
  const settings = computed(() => store.state.authorization.settings);

  /** Route path to redirect to after a successful login. Set before pushing to the login route. */
  const goToAfterLogin = computed(() => store.state.authorization.goToAfterLogin);

  const jwtTokenExpirationDateTime = computed(
    () => store.state.authorization.jwtTokenExpirationDateTime,
  );

  /** IDs of saved searches the user has applied as active filters. */
  const userAppliedSavedSearchIds = computed(
    () => store.state.authorization.userAppliedSavedSearchIds,
  );

  /** Carfax OAuth tokens. `null` when the user has not authenticated with Carfax. */
  const carfaxTokens = computed(
    () => store.state.authorization.carfaxTokens,
  );

  // Getters

  /** Alias of `ready` exposed as a getter. Prefer `ready` for raw state access. */
  const authorizationIsReady = computed(() => store.getters.authorizationIsReady);

  /**
   * Fully constructed `User` model instance decorated with derived flags
   * (`isVCI`, `isLimitedUser`). Prefer this over the raw `user` state in components.
   */
  const currentUser = computed(() => store.getters.currentUser);

  /**
   * Application permission strings decoded from the JWT (e.g. `['buyer', 'vci', 'readonly']`).
   * Use for capability checks. Distinct from `userRole`, which is a single classification string.
   */
  const appRoles = computed(() => store.getters.appRoles);

  /** `true` when a user object is present in state (i.e. a session exists). */
  const isAuthorized = computed(() => store.getters.isAuthorized);

  const isAdmin = computed(() => store.getters.isAdmin);
  const memberType = computed(() => store.getters.memberType);

  /** `true` when the user holds a VCI (Vehicle Condition Inspector) or SIA inspector role. */
  const isVCI = computed(() => store.getters.isVCI);

  /** `true` when the user has the `launch_gtd_sale` permission in their JWT app roles. */
  const isLaunchGtdSale = computed(() => store.getters.isLaunchGtdSale);

  const canSell = computed(() => store.getters.canSell);
  const hasMultipleDealerships = computed(() => store.getters.hasMultipleDealerships);
  const assignedDealershipIds = computed(() => store.getters.assignedDealershipIds);

  /** `true` when the JWT contains the `readonly` role, restricting write operations. */
  const isLimitedUser = computed(() => store.getters.isLimitedUser);

  /**
   * Single role classification string (e.g. `'USER'`, `'ADMIN'`). Defaults to `'USER'`.
   * Distinct from `appRoles`, which is the full list of JWT permission strings.
   */
  const userRole = computed(() => store.getters.userRole);

  /**
   * Selected persistent proxy bid duration from user settings.
   * Defaults to `{ value: '10', description: '10 Days' }`.
   */
  const proxyDuration = computed(() => store.getters.proxyDuration);

  /** `true` when the user has an active ACV credit balance with a non-empty banner message. */
  const hasCreditBalanceInfo = computed(() => store.getters.hasCreditBalanceInfo);

  const showCapitalMenu = computed(() => store.getters.showCapitalMenu);

  /**
   * Returns a `computed` ref for the settings within a given category.
   * Each call produces an independent computed — call once per category and cache the result.
   *
   * @param {string} category - The settings category key (e.g. `'proxy'`).
   * @returns {import('vue').ComputedRef<Array>}
   */
  const userSettings = (category) => computed(() => store.getters.userSettings(category));

  // Actions

  /** @param {{ email: string, password: string, reCaptchaToken: string }} credentials */
  const loginUser = (credentials) => store.dispatch('loginUser', credentials);

  /** @param {{ ssoJwtToken: string }} params */
  const ssoLoginUser = (params) => store.dispatch('ssoLoginUser', params);

  /**
   * Authenticates a user coming from the native mobile app, which provides tokens directly
   * instead of going through the standard login flow.
   *
   * @param {{ userId: number, jwtToken: string, refreshToken: string }} params
   */
  const loginNativeUser = (params) => store.dispatch('loginNativeUser', params);

  /** Logs out from the backend, clears all auth state, and removes tokens from localStorage. */
  const clearUser = () => store.dispatch('clearUser');

  /** Re-fetches user login data and zip code from the API without requiring a full re-login. */
  const refreshUser = () => store.dispatch('refreshUser');

  /**
   * Refreshes JWT tokens if they are older than 12 minutes. Deduplicates concurrent calls —
   * multiple callers awaiting a refresh will share one in-flight request.
   *
   * @param {{ force?: boolean }} [options] - Pass `force: true` to bypass the 12-minute threshold.
   */
  const refreshJwtTokens = (options) => store.dispatch('refreshJwtTokens', options);

  /** Reads tokens synchronously from localStorage. Returns `{}` if no tokens are stored. */
  const getJwtTokens = () => store.dispatch('getJwtTokens');

  /**
   * Persists new JWT tokens to localStorage and derives role flags (`isVCI`, `isLimitedUser`, etc.)
   * from the decoded payload.
   *
   * @param {{ jwtToken: string, refreshToken: string, lastRefreshFailed?: boolean }} tokens
   */
  const setJwtTokens = (tokens) => store.dispatch('setJwtTokens', tokens);

  /** @param {string} path - Route path to redirect to after login. */
  const setGoToAfterLogin = (path) => store.dispatch('setGoToAfterLogin', path);

  const clearGoToAfterLogin = () => store.dispatch('clearGoToAfterLogin');

  /**
   * Fetches and stores user preference settings for the given category.
   *
   * @param {{ category?: string }} params
   */
  const getUserSettings = (params) => store.dispatch('getUserSettings', params);

  const clearUserSettings = () => store.dispatch('clearUserSettings');

  /** @param {number[]} ids */
  const setUserAppliedSavedSearchIds = (ids) => store.dispatch('setUserAppliedSavedSearchIds', ids);

  /**
   * Calls the Capital API to determine whether the user is a Capital customer.
   * Tolerates 404 (non-Capital user) but sets `capitalUserFailed` on any other error
   * so callers can retry or redirect appropriately.
   */
  const setIsCapitalUser = () => store.dispatch('setIsCapitalUser');

  /** Fetches and stores ACV credit balance info onto the user object. */
  const setCreditBalanceInfo = () => store.dispatch('setCreditBalanceInfo');

  /**
   * Marks auth initialization as complete or incomplete. Releasing this flag allows
   * the router's `storageReadyPromise` to resolve and navigation to proceed.
   *
   * @param {boolean} isReady
   */
  const updateReady = (isReady) => store.dispatch('updateReady', isReady);

  /**
   * Fetches a short-lived extended-info token from Identity, used for operations
   * that require additional user claims beyond the standard JWT.
   *
   * @returns {Promise<string>} The raw token string.
   */
  const getExtendedInfoToken = () => store.dispatch('getExtendedInfoToken');

  /** @param {object|null} tokens - Carfax OAuth tokens, or `null` to clear. */
  const setCarfaxTokens = (tokens) => store.dispatch('setCarfaxTokens', tokens);

  const logoutCarfax = () => store.dispatch('logoutCarfax');

  /** Re-hydrates Carfax tokens from IndexedDB (localforage) into Vuex state. */
  const refreshCarfaxTokensFromLocalForage = () => store.dispatch('refreshCarfaxTokensFromLocalForage');

  return {
    // State
    errors,
    user,
    ready,
    settings,
    goToAfterLogin,
    jwtTokenExpirationDateTime,
    userAppliedSavedSearchIds,
    carfaxTokens,
    // Getters
    authorizationIsReady,
    currentUser,
    appRoles,
    isAuthorized,
    isAdmin,
    memberType,
    isVCI,
    isLaunchGtdSale,
    canSell,
    hasMultipleDealerships,
    assignedDealershipIds,
    isLimitedUser,
    userRole,
    userSettings,
    proxyDuration,
    hasCreditBalanceInfo,
    showCapitalMenu,
    // Actions
    loginUser,
    ssoLoginUser,
    loginNativeUser,
    clearUser,
    refreshUser,
    refreshJwtTokens,
    getJwtTokens,
    setJwtTokens,
    setGoToAfterLogin,
    clearGoToAfterLogin,
    getUserSettings,
    clearUserSettings,
    setUserAppliedSavedSearchIds,
    setIsCapitalUser,
    setCreditBalanceInfo,
    updateReady,
    getExtendedInfoToken,
    setCarfaxTokens,
    logoutCarfax,
    refreshCarfaxTokensFromLocalForage,
  };
};

export default useAcvAuth;
